import { Flex, Grid, IconButton, Tooltip } from '@chakra-ui/react'
import { faGear as faGearLight, faRefresh } from '@fortawesome/pro-light-svg-icons'
import { faGear } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { QuickMonthSelector, TimeRangeSelector } from 'BrokerTsLib/src/containers'
import type { UseTimeRangeReturn } from 'BrokerTsLib/src/hooks'
import { useEffect } from 'react'
import { FormProvider, useForm } from 'react-hook-form'

import { OrganizationUserFormControls } from '@/containers/search/shared/OrganizationUserFormControls'

interface StatsOptionsControlsProps extends UseTimeRangeReturn {
  isSettingsOpen: boolean
  toggleSettings: () => void
  reload: (force?: boolean) => void
  isLoadingStats: boolean
  ouId?: number
  userId?: string
  onOrganizationUserChange?: (ouId?: number, userId?: string) => void
  showOrganizationUserControls?: boolean
}

export const StatsOptionsControls = ({
  isSettingsOpen,
  toggleSettings,
  reload,
  isLoadingStats,
  ouId,
  userId,
  onOrganizationUserChange,
  showOrganizationUserControls,
  ...timeRangeProps
}: StatsOptionsControlsProps) => {
  const organizationUserForm = useForm<{ ouId: number, userId: string }>({
    defaultValues: { ouId, userId }
  })

  // Update form when props change
  useEffect(() => {
    organizationUserForm.reset({ ouId, userId })
  }, [ouId, userId, organizationUserForm])

  // Watch for changes and notify parent
  const watchedValues = organizationUserForm.watch()

  useEffect(() => {
    if (onOrganizationUserChange) {
      onOrganizationUserChange(watchedValues.ouId, watchedValues.userId)
    }
  }, [watchedValues.ouId, watchedValues.userId, onOrganizationUserChange])

  return (
    <Flex gap={{ base: 2, md: 6 }} justifyContent='space-between' gridColumn={{ base: '1', lg: 'span 2' }}>
      <Flex gap={2} flexWrap='wrap' w='100%'>
        {!isSettingsOpen ? <QuickMonthSelector {...timeRangeProps} /> :
          <>
            <TimeRangeSelector {...timeRangeProps} />
            {showOrganizationUserControls &&
              <FormProvider {...organizationUserForm}>
                <Grid flex={1} gap={2} gridTemplateColumns='1fr 1fr' minW='250px' maxW='400px'>
                  <OrganizationUserFormControls showLabel={false} size='sm' variant='outline' selectStyle={{
                    border: '1px solid', borderColor: 'primary.500', color: 'primary.600', _hover: { borderColor: 'primary.600', bg: 'gray.50' }
                  }} />
                </Grid>
              </FormProvider>
            }
          </>
        }

        <Flex gap={2} alignItems='end' ml='auto' pl={4}>
          <Tooltip label='Nastavení období' hasArrow openDelay={500}>
            <IconButton
              aria-label={isSettingsOpen ? 'Zavřít nastavení období' : 'Otevřít nastavení období'}
              icon={<FontAwesomeIcon icon={isSettingsOpen ? faGear : faGearLight} size="lg" />}
              variant="ghost"
              size='lg'
              py={2}
              px={3}
              onClick={toggleSettings}
            />
          </Tooltip>

          <Tooltip label='Aktualizovat' hasArrow openDelay={500}>
            <IconButton
              aria-label='Aktualizovat'
              css={{
                '--fa-animation-duration': '0.5s',
              }}
              icon={<FontAwesomeIcon icon={faRefresh} spin={isLoadingStats} size='lg' />}
              variant="ghost"
              size='lg'
              py={2}
              px={3}
              onClick={() => { reload(true) }}
            />
          </Tooltip>
        </Flex>
      </Flex>
    </Flex>
  )
}
